import * as React from "react"
import { LocalStorageItems } from "@modules/localStorage/constants"

// url to Gatsby Cloud public status api by Statuspage
const API_BASE = `https://rp2m7fz90dpy.statuspage.io/api`
// we fetch api not more often than the value
const FETCH_INTERVAL = 60000 // 1min

export type StatusPageData = {
  status: {
    indicator: string
    description: string
  }
  incidents: { name: string }[]
}

export type StatusData = StatusPageData & { timestamp: number }

export function useStatusPageStatus(): StatusData | null {
  const [data, setData] = React.useState<StatusData | null>(null)

  async function getData() {
    try {
      const data = await fetchStatus()

      const newData = {
        ...data,
        timestamp: new Date().getTime(),
      }

      setData(newData)

      localStorage.setItem(
        LocalStorageItems.GatsbyCloudStatusPage,
        JSON.stringify(newData)
      )
    } catch (e) {
      if (window.console && console.warn) {
        console.warn("Failed to fetch data from statuspage", e)
      }
    }
  }

  React.useEffect(() => {
    const now = new Date().getTime()

    // initial check, read and set data at screen/component mount
    if (!data) {
      const lastStatusDataRaw = localStorage.getItem(
        LocalStorageItems.GatsbyCloudStatusPage
      )
      const lastStatusData = lastStatusDataRaw && JSON.parse(lastStatusDataRaw)

      if (lastStatusData) {
        if (now - lastStatusData.timestamp > FETCH_INTERVAL) {
          getData()
        } else {
          setData(lastStatusData)
        }
      } else {
        getData()
      }
    }

    // StatusPageBanner is re-rendered only if a user switch workspace or site
    // so for cases the user stays inside one site longer than FETCH_INTERVAL
    // we manage data re-fetching based on setInterval
    const fetchInterval = setInterval(() => {
      getData()
    }, FETCH_INTERVAL)

    return () => {
      clearInterval(fetchInterval)
    }
  }, [])

  return data
}

export async function fetchStatus() {
  const response = await fetch(`${API_BASE}/v2/summary.json`)
  return await response.json()
}
